import React, { useState } from 'react';
import { AdvancedMarker, AdvancedMarkerProps } from '@vis.gl/react-google-maps';
import { MapPin } from '../../MapPin';
import {
  CourierReturnStatusType,
  CourierVehicleType,
} from '@src/pages/Deliveries/context/couriers';
import MapCard from '../../MapCard';
import { globalStyles } from 'snacks-design-system';
import { mapPinColorGenerator } from '../../MapPin/helpers';
import PIEText from '@src/shared/components/PIEText';
import DashboardVehicleSelector from '@src/pages/DeliveryDashboard/components/DashboardVehicleSelector';
import { useTranslation } from 'react-i18next';
import styles from './CourierMapMarker.module.scss';
import useEtaBack from '@src/pages/DeliveryDashboard/hooks/use-eta-back';
import useDashboardComputedFunctions from '@src/pages/DeliveryDashboard/hooks/use-dashboard-computed';
import PIETag, { ETagsColorType } from '@src/shared/components/PIETag';
import { IServerTime } from '@src/pages/DeliveryDashboard/models/model-interface';
import dayjs from 'dayjs';
import useRenderPhase from '@src/shared/hooks/use-render-phase';
import { useCommonPersistentStore } from '@src/shared/store/common-persistent-store';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
interface OrderMapMarkerProps {
  vehicleType: CourierVehicleType;
  courierReturnStatusType: CourierReturnStatusType;
  courierName: string;
  courierId: string;
  etaTime?: string | undefined | boolean;
  dueTime?: string | undefined | boolean;
  compareTime?: IServerTime;
  onClick: () => void;
}

const { xdsColorContentLight, xdsColorSupportPositive } = globalStyles;

export const CourierMapMarker = ({
  courierName,
  vehicleType,
  courierReturnStatusType,
  courierId,
  onClick,
  etaTime,
  compareTime,
  dueTime,
}: AdvancedMarkerProps & OrderMapMarkerProps) => {
  const getPinColorSchema = () => {
    const defaultCourierColorCombination = mapPinColorGenerator('#3C4C4F', xdsColorContentLight);
    const returningCourierColorCombination = mapPinColorGenerator(
      xdsColorSupportPositive,
      xdsColorContentLight
    );
    return courierReturnStatusType !== CourierReturnStatusType.Returning
      ? defaultCourierColorCombination
      : returningCourierColorCombination;
  };

  const { t } = useTranslation();
  const { getCourierEtaById } = useDashboardComputedFunctions();
  const { getDistanceDuration } = useEtaBack();
  const { getCourierLocation } = useDashboardComputedFunctions();
  const locationData = getCourierLocation(courierId) as any;
  const { latitude = null, longitude = null } = locationData || {};
  const [etaTimeCompareValue, setEtaTimeCompareValue] = useState<string | false>(false);
  const { persistentOrderEtaListUpdateDate, setPersistentOrderEtaListUpdateDate } =
    useCommonPersistentStore();
  const getTagColor = () => {
    return courierReturnStatusType !== CourierReturnStatusType.Returning
      ? ETagsColorType.Neutral
      : ETagsColorType.Success;
  };

  const howManyMinutesLeft = (baseTime: any, serverTime: any) => {
    return dayjs(serverTime).to(dayjs(baseTime), true);
  };
  const getCardContent = () => {
    let content = '';

    const minutesLeft = howManyMinutesLeft(etaTime || dueTime, etaTimeCompareValue);
    content =
      courierReturnStatusType === CourierReturnStatusType.Returning
        ? getDistanceDuration(getCourierEtaById(courierId))
        : etaTime
        ? t('courier.delivering_in') + ` ${minutesLeft || ''}`
        : '';

    return (
      <>
        <PIEComponentUtil marginTop={!content ? '' : 'var(--xds-spacing-a)'}>
          <PIEText
            size="var(--xds-font-size-14)"
            fontWeight="var(--xds-font-weight-bold)"
            text={courierName}
            align="center"
          />
        </PIEComponentUtil>

        {content && (
          <PIETag mode={getTagColor()} className={styles.CourierMapMarkerDeliveredTag}>
            <PIEText
              size="var(--xds-font-size-14)"
              fontWeight="var(--xds-font-weight-bold)"
              text={content}
              align="center"
            />
          </PIETag>
        )}
      </>
    );
  };

  const isLocationDataReady = () => {
    return !!(latitude && longitude);
  };

  useRenderPhase(
    () => {
      //initial render
      if (persistentOrderEtaListUpdateDate) {
        setEtaTimeCompareValue(persistentOrderEtaListUpdateDate);
      } else {
        setEtaTimeCompareValue(compareTime as any);
      }
    },
    () => {
      //subsequent renders
      setPersistentOrderEtaListUpdateDate(compareTime as any);
      setEtaTimeCompareValue(compareTime as any);
    },
    [etaTime]
  );

  return (
    (isLocationDataReady() && (
      <>
        <AdvancedMarker position={{ lat: latitude, lng: longitude }} onClick={() => onClick()}>
          <MapCard className={styles.mapMarkerCard}>{getCardContent()}</MapCard>
        </AdvancedMarker>
        <AdvancedMarker position={{ lat: latitude, lng: longitude }} onClick={() => onClick()}>
          <MapPin {...getPinColorSchema()}>
            <DashboardVehicleSelector vehicleType={vehicleType} />
          </MapPin>
        </AdvancedMarker>
      </>
    )) || <></>
  );
};

export default CourierMapMarker;
